import { useState, type MouseEvent, type ReactNode } from 'react';
import { Box, IconButton, Stack } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useReducedMotion } from '@/common/presentation/hooks/useReducedMotion';
import { LayersRowActionTooltip } from './LayersRowActionTooltip';
import { LayersRowMenu } from './LayersRowMenu';
import type { LayersRowDragHandle, LayersRowEye, LayersRowMenuItem } from './layersTypes';
import { LayersRowRevealedGroup } from './LayersRowRevealedGroup';

const ACTION_BUTTON_SX = {
  width: 'var(--size-24)',
  height: 'var(--size-24)',
  borderRadius: 'var(--border-radius-none)',
  color: 'var(--icon-neutral-interactive)',
  // MUI ButtonBase sets `outline: 0` (emotion, injected after the DS stylesheet), which beat the DS's global
  // `:focus-visible` ring: the eye, drag handle, move and ⋮ buttons showed no keyboard focus. `Mui-focusVisible`
  // is the class MUI itself adds for keyboard focus, so the ring is back exactly there (WCAG 2.4.7).
  '&.Mui-focusVisible': { outline: 'var(--border-width-sm) solid var(--border-focus)', outlineOffset: -2 }
} as const;

const SPACER_SX = { width: 'var(--size-24)', height: 'var(--size-24)', flexShrink: 0 } as const;

/** A control that takes no width while its row is idle (`collapseIdleActions`); it stays focusable. */
const COLLAPSED_SX = { width: 0, minWidth: 0, p: 0, m: 0, overflow: 'hidden' } as const;

// Touch has no `:hover`, so a reveal gated only on pointer state would leave the row's actions
// permanently invisible on a touch device — `@media (hover: none)` keeps them shown there always.
// Under `prefers-reduced-motion: reduce` the fade is dropped (DB-12b-4): the actions switch instantly.
const revealTransition = (reducedMotion: boolean) =>
  ({
    transition: reducedMotion ? 'none' : 'opacity var(--duration-fast) ease',
    '@media (hover: none)': { opacity: 1 }
  }) as const;

export interface LayersRowActionsProps {
  name: string;
  disabled?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  revealedTrailing?: ReactNode;
  dragHandle?: LayersRowDragHandle;
  eye?: LayersRowEye;
  menuItems?: LayersRowMenuItem[];
  menuDisabledReason?: string;
  menuTestId?: string;
  testId: string;
  /** From `useLayersRowReveal` — whether the hover/drag/⋮ cluster should be visible right now. */
  revealed: boolean;
  /** The idle row's move / eye / ⋮ controls take no width (see `LayersRowProps.collapseIdleActions`). */
  collapseIdle?: boolean;
}

/**
 * The row's trailing action cluster: drag handle, move up/down (absent arrows keep their slot so
 * every row's controls stay in one column), the visibility eye, and the ⋮ overflow. The drag
 * handle stays visible so a row can be reordered without hovering. Move up/down and the menu fade
 * with `revealed`. The eye stays visible whenever it is switched off.
 *
 * The eye is a toggle button with ONE stable accessible name ("Hide {name}"); its state is carried
 * by `aria-pressed` alone (pressed = hidden, like a mute button) — the label is never swapped, so
 * assistive tech does not announce a changed name AND a changed state (LLAMS-610 G-14). Only the
 * sighted tooltip still reads "Show" while the row is hidden.
 */
export function LayersRowActions({
  name,
  disabled = false,
  onMoveUp,
  onMoveDown,
  revealedTrailing,
  dragHandle,
  eye,
  menuItems,
  menuDisabledReason,
  menuTestId,
  testId,
  revealed,
  collapseIdle = false
}: LayersRowActionsProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const hasMenu = Boolean(menuItems?.length);
  const reducedMotion = useReducedMotion();
  const idleSx = collapseIdle && !revealed ? COLLAPSED_SX : {};
  const spacerSx = collapseIdle && !revealed ? { ...SPACER_SX, ...COLLAPSED_SX } : SPACER_SX;
  const revealSx = { ...revealTransition(reducedMotion), opacity: revealed ? 1 : 0, ...idleSx };
  const eyeAlwaysShown = eye ? eye.alwaysVisible || !eye.visible : false;

  const openMenu = (event: MouseEvent<HTMLButtonElement>) => {
    if (menuDisabledReason) return;
    setMenuAnchor(event.currentTarget);
  };

  return (
    <Stack direction="row" spacing={0} alignItems="center" sx={{ flexShrink: 0 }}>
      {revealedTrailing ? <LayersRowRevealedGroup revealed={revealed}>{revealedTrailing}</LayersRowRevealedGroup> : null}

      {dragHandle ? (
        <IconButton
          size="small"
          disabled={disabled}
          draggable={dragHandle.onDragStart ? true : undefined}
          onDragStart={dragHandle.onDragStart}
          {...(dragHandle.handleProps ?? {})}
          aria-label={`Drag to reorder — ${dragHandle.label ?? name}`}
          sx={{ ...ACTION_BUTTON_SX, cursor: 'grab', touchAction: 'none', opacity: 1 }}
        >
          <DragIndicatorIcon sx={{ fontSize: 'var(--size-16)' }} />
        </IconButton>
      ) : null}

      {onMoveUp ? (
        <LayersRowActionTooltip title="Move up">
          <IconButton
            size="small"
            disabled={disabled}
            onClick={onMoveUp}
            aria-label={`Move up — ${name}`}
            sx={{ ...ACTION_BUTTON_SX, ...revealSx }}
          >
            <ArrowUpwardIcon sx={{ fontSize: 'var(--size-16)' }} />
          </IconButton>
        </LayersRowActionTooltip>
      ) : (
        <Box aria-hidden sx={spacerSx} />
      )}

      {onMoveDown ? (
        <LayersRowActionTooltip title="Move down">
          <IconButton
            size="small"
            disabled={disabled}
            onClick={onMoveDown}
            aria-label={`Move down — ${name}`}
            sx={{ ...ACTION_BUTTON_SX, ...revealSx }}
          >
            <ArrowDownwardIcon sx={{ fontSize: 'var(--size-16)' }} />
          </IconButton>
        </LayersRowActionTooltip>
      ) : (
        <Box aria-hidden sx={spacerSx} />
      )}

      {eye ? (
        <LayersRowActionTooltip title={eye.visible ? 'Hide' : 'Show'}>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={() => eye.onToggle(!eye.visible)}
            aria-label={`Hide ${name}`}
            aria-pressed={!eye.visible}
            data-testid={eye.testId}
            sx={{ ...ACTION_BUTTON_SX, ...(eyeAlwaysShown ? {} : revealSx) }}
          >
            {eye.visible ? (
              <VisibilityOutlinedIcon sx={{ fontSize: 'var(--size-16)' }} />
            ) : (
              <VisibilityOffOutlinedIcon sx={{ fontSize: 'var(--size-16)' }} />
            )}
          </IconButton>
        </LayersRowActionTooltip>
      ) : null}

      {hasMenu ? (
        <>
          <LayersRowActionTooltip title={menuDisabledReason ?? ''}>
            <IconButton
              size="small"
              disabled={disabled}
              aria-disabled={menuDisabledReason ? true : undefined}
              onClick={openMenu}
              aria-label={`More actions — ${name}`}
              aria-haspopup="menu"
              aria-expanded={Boolean(menuAnchor)}
              data-testid={menuTestId ?? `${testId}:menu`}
              sx={{
                ...ACTION_BUTTON_SX,
                ...revealSx,
                ...(menuDisabledReason ? { color: 'var(--icon-disabled)', cursor: 'not-allowed' } : {})
              }}
            >
              <MoreVertIcon sx={{ fontSize: 'var(--size-16)' }} />
            </IconButton>
          </LayersRowActionTooltip>
          <LayersRowMenu
            anchorEl={menuAnchor}
            onClose={() => setMenuAnchor(null)}
            items={menuItems ?? []}
            testId={menuTestId ?? `${testId}:menu`}
          />
        </>
      ) : (
        <Box aria-hidden sx={spacerSx} />
      )}
    </Stack>
  );
}
